import React, { useState, useEffect } from "react";
import { Box, useTheme, InputBase, IconButton } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import SearchIcon from "@mui/icons-material/Search";
import { putCamionesPedidos, getLiberarOrdenCarga } from "../../services/pedidoVenta.services";
import { patchOrdenCargaStatus } from "../../services/ordenCarga.services";
import SendIcon from "@mui/icons-material/Send";
import ModalCharge from "../../modal/modalCharge";
import LoadingSpinner from "../../loadingSpinner";
import ModalError from "../../modal/modalError";
import ModalLiberarOrden from "../../modal/cargamento/ModalLiberarOrden";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import MenuIcon from "@mui/icons-material/Menu";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

const LiberarCamion = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [registrationError, setRegistrationError] = useState(false);
  const [error, setError] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalLiberarOpen, setModalLiberarOpen] = useState(false);
  const [ordenALiberar, setOrdenALiberar] = useState(null);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const fetchData = async () => {
    try {
      const response = await getLiberarOrdenCarga();
      setData(response);
      setFilteredData(response);
    } catch (err) {
      console.error("Error al obtener órdenes:", err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchValue(value);
    const lower = value.toLowerCase();
    setFilteredData(
      data.filter(
        (item) =>
          (item.nro_orden ?? "").toLowerCase().includes(lower) ||
          (item.chapa ?? "").toLowerCase().includes(lower) ||
          (item.motorista_nombre ?? "").toLowerCase().includes(lower)
      )
    );
  };

  const getRowClassName = (params) =>
    params.row.status === "EN_ESPERA" ? "row-disabled" : "";

  const columns = [
    {
      field: "menu",
      headerName: "",
      flex: 0.2,
      renderCell: (params) => (
        <IconButton onClick={(e) => handleMenuClick(e, params.row)}>
          <MenuIcon />
        </IconButton>
      ),
    },
    { field: "nro_orden", headerName: "Orden", flex: 1 },
    {
      field: "fecha_carga",
      headerName: "Fecha",
      flex: 0.8,
      valueFormatter: ({ value }) =>
        value ? new Date(value).toLocaleDateString() : "-",
    },
    { field: "chapa", headerName: "Vehículo", flex: 0.8 },
    { field: "motorista_nombre", headerName: "Motorista", flex: 1 },
    { field: "total_asignado", headerName: "Asignado", flex: 0.6, type: "number" },
    { field: "total_cargado", headerName: "Cargado", flex: 0.6, type: "number" },
    {
      field: "acciones",
      headerName: "Liberar",
      flex: 0.6,
      renderCell: (params) => {
        const { total_cargado, total_asignado, total_entregas, status } = params.row;
        const disabled =
          status === "EN_ESPERA" ||
          parseInt(total_entregas) === 0 ||
          parseInt(total_cargado) < parseInt(total_asignado);
        return (
          <IconButton
            onClick={() => handleAbrirLiberar(params.row)}
            disabled={disabled}
          >
            <SendIcon />
          </IconButton>
        );
      },
    },
  ];

  const handleAbrirLiberar = (row) => {
    setOrdenALiberar(row);
    setModalLiberarOpen(true);
  };

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const runMenuAction = async (fn) => {
    setIsLoading(true);
    handleMenuClose();
    try {
      await new Promise((r) => setTimeout(r, 1500));
      await fn();
      fetchData();
    } catch (err) {
      setError(err.message);
      setRegistrationError(true);
    } finally {
      setTimeout(() => setIsLoading(false), 1500);
    }
  };

  const handleEspera = () =>
    runMenuAction(async () => {
      await patchOrdenCargaStatus(selectedRow.id, "EN_ESPERA");
      await putCamionesPedidos(selectedRow.id_vehiculo, { status_active: 4 });
      toast.success(`Orden ${selectedRow.nro_orden} en espera`, {
        position: "top-center", autoClose: 5000, hideProgressBar: true,
        closeOnClick: true, pauseOnHover: true, draggable: true,
      });
    });

  const handleActivar = () =>
    runMenuAction(async () => {
      await patchOrdenCargaStatus(selectedRow.id, "EN_CARGA");
      await putCamionesPedidos(selectedRow.id_vehiculo, { status_active: 1 });
      toast.success(`Orden ${selectedRow.nro_orden} desbloqueada`, {
        position: "top-center", autoClose: 5000, hideProgressBar: true,
        closeOnClick: true, pauseOnHover: true, draggable: true,
      });
    });

  const handleCancelar = () =>
    runMenuAction(async () => {
      await patchOrdenCargaStatus(selectedRow.id, "ANULADA");
      await putCamionesPedidos(selectedRow.id_vehiculo, { status_active: 0 });
      toast.success(`Orden ${selectedRow.nro_orden} anulada`, {
        position: "top-center", autoClose: 5000, hideProgressBar: true,
        closeOnClick: true, pauseOnHover: true, draggable: true,
      });
    });

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" p={0}>
        <Header title="Aguardando Liberación" subtitle="Liberar Orden de Carga" />
        <Box display="flex" alignItems="center">
          <Box
            display="flex"
            backgroundColor={colors.primary[400]}
            borderRadius="3px"
            height="40%"
            width="60%"
            ml={1}
          >
            <InputBase
              sx={{ ml: 2, flex: 1 }}
              placeholder="Buscar orden / vehículo / motorista"
              value={searchValue}
              onChange={handleSearchChange}
            />
            <IconButton type="button" sx={{ p: 1 }}>
              <SearchIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[900],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[900],
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
          "& .row-disabled": { opacity: 0.5 },
        }}
      >
        <DataGrid
          rows={filteredData}
          columns={columns}
          components={{ Toolbar: GridToolbar }}
          getRowId={(row) => row.id}
          getRowClassName={getRowClassName}
        />
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEspera}>En Espera</MenuItem>
        <MenuItem onClick={handleActivar}>Desbloquear</MenuItem>
        <MenuItem onClick={handleCancelar}>Anular</MenuItem>
      </Menu>

      <ModalLiberarOrden
        open={modalLiberarOpen}
        onClose={() => setModalLiberarOpen(false)}
        orden={ordenALiberar}
        onLiberado={fetchData}
      />

      <ModalCharge isLoading={isLoading} />
      {isLoading && <LoadingSpinner />}
      <ModalError open={registrationError} onClose={() => setRegistrationError(false)} error={error} />
    </Box>
  );
};

export default LiberarCamion;
